import URN from "@ppb/tbd-store/state/layout/URN";
import { MapStateToPropsFactory } from "react-redux";
import { createSelector } from "reselect";
import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { MarketCards } from "@ppb/tbd-store/state/layout/cards/Card.types";
import { areObjectsDeepEqual } from "@ppb/tbd-store/helpers/obj-deep-equal-comparator";
import { MarketCardExpansionBehavior } from "@ppb/tbd-store/clients/catalogue/catalogue-response-types";
import { createCardByURNSelector } from "@ppb/tbd-store/state/layout/cards/cards-selectors";
import { CardHeaderSize, CardTheme } from "@ppb/the-wall-common/types/Card/Card.types";
import { ContainerProps as MarketCardPropsProps } from "../Market/map-to-props-factory";

export type CardProps = MarketCardPropsProps;

export type StateProps = MarketCardPropsProps | Record<string, never>;

export type ContainerProps = {
  urn: URN;
  visible?: boolean;
};

// Card payloads are re-delivered on navigation and lazy loading, which replaces the card entity with a
// structurally identical copy. Handing the nested objects through as-is gives Market and SportsbookMarket
// new references for unchanged data, so each one is kept stable until its content actually changes.
const createStableValueSelector = <T>() =>
  createSelector(
    (value: T) => value,
    (value) => value,
    { memoizeOptions: { resultEqualityCheck: areObjectsDeepEqual } },
  );

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, ApplicationState> = () => {
  const getMarketCardByURN = createCardByURNSelector<MarketCards, URN>();
  const getDisplayRunners = createStableValueSelector<CardProps["displayRunners"]>();
  const getMarketViewLinks = createStableValueSelector<CardProps["marketViewLinks"]>();
  const getRunnerViewLinks = createStableValueSelector<CardProps["runnerViewLinks"]>();
  const getMarketPromo = createStableValueSelector<CardProps["marketPromo"]>();
  const getInfoBlurbs = createStableValueSelector<CardProps["infoBlurbs"]>();

  return (state: ApplicationState, { urn }: ContainerProps): StateProps => {
    const marketCard = getMarketCardByURN(state.layouts.cards.markets, urn);

    if (!marketCard) {
      return {};
    }

    const hasColoredMarketCardHeader = state.entities.brandSettings?.COLORED_MARKET_CARD_HEADER;

    return {
      title: marketCard.title,
      displayRunners: getDisplayRunners(marketCard.displayRunners),
      cardUrn: urn,
      marketViewLinks: getMarketViewLinks(marketCard.viewLinks),
      runnerViewLinks: getRunnerViewLinks(marketCard.runnerViewLinks),
      isRunnerExpandable: marketCard.isRunnerExpandable,
      template: marketCard.template,
      numberOfItemsToDisplay: marketCard.numberOfItemsToDisplay,
      marketPromo: getMarketPromo(marketCard.marketPromo),
      infoBlurbs: getInfoBlurbs(marketCard.infoBlurbs),
      isCollapsible:
        marketCard.expansionBehavior === MarketCardExpansionBehavior.ExpandedByDefault ||
        marketCard.expansionBehavior === MarketCardExpansionBehavior.CollapsedByDefault,
      startOpen: marketCard.expansionBehavior !== MarketCardExpansionBehavior.CollapsedByDefault,
      ...(hasColoredMarketCardHeader && { cardTheme: CardTheme.SECONDARY, cardHeaderSize: CardHeaderSize.LARGE }),
    };
  };
};

export type DispatchProps = Record<string, never>;
